import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, HelpCircle, Bot, User, ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { submitSupportInquiry } from '../services/api';

// FAQ database with categories
const faqData = {
  course: [
    {
      question: "How long is the course?",
      answer: "The full course is 40+ hours spread across 8 modules. Most learners complete it in 8-10 weeks at 4-5 hours per week. You can go at your own pace!"
    },
    {
      question: "Is it really free?",
      answer: "Yes! The entire course is 100% free with no hidden fees, no credit card required, and no upsells. We believe everyone deserves access to quality safety training."
    },
    {
      question: "What topics are covered?",
      answer: "Our 8 modules cover: Hazard Communication, Personal Protective Equipment (PPE), Emergency Action Plans, Fire Safety, Ergonomics, Electrical Safety, Walking-Working Surfaces, and a Final Certification Review."
    },
    {
      question: "Can I access on mobile?",
      answer: "Absolutely! The course is fully responsive and works on desktop, tablet, and mobile devices. Learn anywhere, anytime."
    }
  ],
  certification: [
    {
      question: "Do I get a certificate?",
      answer: "Yes! Upon completing all 8 modules with passing quiz scores (80%+), you'll receive a verifiable digital certificate of completion that you can share with employers."
    },
    {
      question: "Is it OSHA certified?",
      answer: "This course covers OSHA-aligned content and follows federal safety guidelines, but is not an official OSHA certification. It prepares you for workplace compliance and safety best practices."
    },
    {
      question: "How do I verify my certificate?",
      answer: "Each certificate has a unique verification number. Employers can verify certificates at our verification page (/verify) by entering the certificate number."
    },
    {
      question: "Does the certificate expire?",
      answer: "Your certificate does not expire. However, we recommend refreshing your training annually as OSHA regulations may change."
    }
  ],
  technical: [
    {
      question: "How do I save my progress?",
      answer: "Your progress is automatically saved when you complete modules or quizzes. Just make sure you're logged in with your Google account to track everything."
    },
    {
      question: "I forgot my login details",
      answer: "We use Google authentication, so you can sign in with any Google account. Your progress is linked to your Google email address."
    },
    {
      question: "The quiz isn't loading",
      answer: "Try refreshing the page or clearing your browser cache. If issues persist, try a different browser. Our course works best on Chrome, Firefox, Safari, or Edge."
    },
    {
      question: "Can I retake quizzes?",
      answer: "Yes! You can retake quizzes as many times as needed. Only your highest score is recorded for certification purposes."
    }
  ]
};

const categories = [
  { id: 'course', label: 'Course Info', icon: '📚' },
  { id: 'certification', label: 'Certification', icon: '🏆' },
  { id: 'technical', label: 'Technical Help', icon: '⚙️' },
  { id: 'contact', label: 'Contact Support', icon: '✉️' }
];

const FloatingHelp = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('categories'); // categories, faq, chat, contact, success
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleOpen = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      // Initial greeting
      setTimeout(() => {
        setMessages([{
          type: 'bot',
          text: "Hi there! 👋 I'm your OSHA training assistant. How can I help you today?"
        }]);
      }, 300);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleCategorySelect = (categoryId) => {
    if (categoryId === 'contact') {
      setView('contact');
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "I'd be happy to connect you with our support team. Please fill out the form below and we'll get back to you within 24 hours."
      }]);
    } else {
      setSelectedCategory(categoryId);
      setView('faq');
      const categoryLabel = categories.find(c => c.id === categoryId)?.label;
      setMessages(prev => [...prev, {
        type: 'user',
        text: `I have questions about ${categoryLabel}`
      }]);
      setTimeout(() => {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          setMessages(prev => [...prev, {
            type: 'bot',
            text: `Great! Here are common questions about ${categoryLabel}. Click any question to get an answer:`
          }]);
        }, 800);
      }, 300);
    }
  };

  const handleQuestionSelect = (faq) => {
    setMessages(prev => [...prev, {
      type: 'user',
      text: faq.question
    }]);
    
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        type: 'bot',
        text: faq.answer
      }]);
    }, 1000);
  };

  const handleBack = () => {
    if (view === 'faq') {
      setView('categories');
      setSelectedCategory(null);
    } else if (view === 'contact' || view === 'success') {
      setView('categories');
      setContactForm({ name: '', email: '', message: '' });
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) return;
    
    setIsSubmitting(true);
    try {
      await submitSupportInquiry({
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message,
        category: 'general'
      });
      setView('success');
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "Thank you! Your message has been sent. Our team will respond to your email within 24 hours. 📧"
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "Sorry, there was an error sending your message. Please try again or email us directly at support@wsafe.ai"
      }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetChat = () => {
    setView('categories');
    setSelectedCategory(null);
    setContactForm({ name: '', email: '', message: '' });
    setMessages([{
      type: 'bot',
      text: "Hi there! 👋 I'm your OSHA training assistant. How can I help you today?"
    }]);
  };

  return (
    <>
      {/* Chat window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-in slide-in-from-bottom-4"
          data-testid="floating-help-chat"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {view !== 'categories' && (
                  <button 
                    onClick={handleBack}
                    className="p-1 hover:bg-white/20 rounded transition-colors mr-1"
                    data-testid="chat-back-btn"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold">Safety Assistant</h4>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse"></span>
                    <p className="text-xs text-emerald-100">Online</p>
                  </div>
                </div>
              </div>
              <button 
                onClick={handleClose}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className="h-72 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((msg, index) => (
              <div 
                key={index} 
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-end gap-2 max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.type === 'user' ? 'bg-emerald-500' : 'bg-slate-700'
                  }`}>
                    {msg.type === 'user' ? (
                      <User className="w-3.5 h-3.5 text-white" />
                    ) : (
                      <Bot className="w-3.5 h-3.5 text-white" />
                    )}
                  </div>
                  <div className={`rounded-2xl px-4 py-2.5 ${
                    msg.type === 'user' 
                      ? 'bg-emerald-500 text-white rounded-br-md' 
                      : 'bg-white text-slate-700 rounded-bl-md shadow-sm border border-slate-100'
                  }`}>
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-end gap-2">
                  <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center">
                    <Bot className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-slate-100">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Action area */}
          <div className="p-4 border-t border-slate-200 bg-white">
            {view === 'categories' && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-3">Select a topic:</p>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category.id)}
                      className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all text-left"
                      data-testid={`category-${category.id}`}
                    >
                      <span className="text-lg">{category.icon}</span>
                      <span className="text-sm font-medium text-slate-700">{category.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {view === 'faq' && selectedCategory && (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {faqData[selectedCategory]?.map((faq, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuestionSelect(faq)}
                    className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all text-sm text-slate-700"
                    data-testid={`faq-question-${index}`}
                  >
                    {faq.question}
                  </button>
                ))}
                <button
                  onClick={() => handleCategorySelect('contact')}
                  className="w-full text-center p-2 text-emerald-600 text-sm hover:underline"
                >
                  Still have questions? Contact support →
                </button>
              </div>
            )}

            {view === 'contact' && (
              <form onSubmit={handleContactSubmit} className="space-y-3">
                <Input
                  placeholder="Your name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({...prev, name: e.target.value}))}
                  className="text-sm"
                  required
                  data-testid="contact-name"
                />
                <Input
                  type="email"
                  placeholder="Your email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({...prev, email: e.target.value}))}
                  className="text-sm"
                  required
                  data-testid="contact-email"
                />
                <Textarea
                  placeholder="How can we help?"
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({...prev, message: e.target.value}))}
                  className="text-sm resize-none"
                  rows={3}
                  required
                  data-testid="contact-message"
                />
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}

            {view === 'success' && (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-sm text-slate-600 mb-3">Message sent successfully!</p>
                <Button 
                  onClick={resetChat}
                  variant="outline"
                  className="text-sm"
                  data-testid="start-new-chat"
                >
                  Start new conversation
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={isOpen ? handleClose : handleOpen}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg z-50 flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-slate-700 hover:bg-slate-600 rotate-0' 
            : 'bg-emerald-500 hover:bg-emerald-600 hover:scale-110'
        }`}
        data-testid="floating-help-btn"
        aria-label={isOpen ? "Close help chat" : "Open help chat"}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Notification badge when closed */}
      {!isOpen && (
        <span className="fixed bottom-[72px] right-6 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold z-50 animate-pulse">
          ?
        </span>
      )}
    </>
  );
};

export default FloatingHelp;
